import { useState, useEffect, useCallback } from 'preact/hooks'
import type { CloudNote } from '../../types'
import { listCloudNotes, upsertCloudNote, deleteCloudNote } from '../../services/api'
import { formatDate, EmptyState, DetailRow, cardStyle, detailPanelStyle, listAreaStyle, refreshBtnStyle } from './shared'
import { ConfirmDialog, ModalDialog, FormField, SaveButton, DeleteButton, CreateButton, InlineInput } from './CrudHelpers'

/**
 * Follow-up family note types — all rendered together in this view, matching
 * the Flutter app's `followups_and_alerts_screen.dart`:
 *   4  Follow-up
 *   6  Postpone reminder
 *   9  Proactive reminder
 *  10  Morning briefing
 */
const FOLLOWUP_TYPE = 4
const FOLLOWUP_FAMILY_TYPES = [4, 6, 9, 10]

type CallsTab = 'active' | 'history'

/** Parse follow-up content JSON, returns null if not valid JSON */
function parseFollowUpContent(content?: string): Record<string, unknown> | null {
  if (!content) return null
  try {
    const parsed = JSON.parse(content)
    return typeof parsed === 'object' && parsed !== null ? parsed : null
  } catch {
    return null
  }
}

/** Match the Flutter app: history = parsed content has completed=true or missed=true */
function isHistoryFollowUp(note: CloudNote): boolean {
  const parsed = parseFollowUpContent(note.content)
  if (!parsed) return false
  return parsed.completed === true || parsed.missed === true
}

/** Parse "YYYY-MM-DD HH:MM" or similar from parsed content into ms; falls back to scheduled_at */
function getFollowUpTime(note: CloudNote): number {
  const parsed = parseFollowUpContent(note.content)
  if (parsed && (parsed.date || parsed.time)) {
    const t = Date.parse(`${parsed.date as string} ${(parsed.time as string) || '00:00'}`)
    if (!isNaN(t)) return t
  }
  const meta = note.metadata as Record<string, unknown> | undefined
  const scheduled = meta?.scheduled_at as string | undefined
  if (scheduled) {
    const t = Date.parse(scheduled)
    if (!isNaN(t)) return t
  }
  return 0
}

export function CallsView({ focusId }: { focusId?: string } = {}) {
  const [notes, setNotes] = useState<CloudNote[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedNote, setSelectedNote] = useState<CloudNote | null>(null)
  const [editingNote, setEditingNote] = useState<CloudNote | null>(null)
  const [isDirty, setIsDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [showCreate, setShowCreate] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [tab, setTab] = useState<CallsTab>('active')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      // Fetch all four follow-up-family types in parallel and merge.
      const lists = await Promise.all(FOLLOWUP_FAMILY_TYPES.map((t) => listCloudNotes(t)))
      setNotes(lists.flat())
    } catch (e) {
      console.error('Failed to load follow-ups:', e)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  // Auto-select the focused follow-up; switch tab if it's in the other one.
  useEffect(() => {
    if (!focusId || notes.length === 0) return
    const match = notes.find((n) => n.id === focusId)
    if (!match) return
    if (selectedNote?.id !== match.id) {
      setTab(isHistoryFollowUp(match) ? 'history' : 'active')
      setSelectedNote(match)
      setEditingNote({ ...match })
      setIsDirty(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusId, notes])

  const activeNotes = notes
    .filter((n) => !isHistoryFollowUp(n))
    .sort((a, b) => getFollowUpTime(a) - getFollowUpTime(b))
  const historyNotes = notes
    .filter(isHistoryFollowUp)
    .sort((a, b) => getFollowUpTime(b) - getFollowUpTime(a))

  const handleSelect = (note: CloudNote) => {
    if (selectedNote?.id === note.id) {
      setSelectedNote(null)
      setEditingNote(null)
      setIsDirty(false)
    } else {
      setSelectedNote(note)
      setEditingNote({ ...note })
      setIsDirty(false)
    }
  }

  const updateField = (field: string, value: unknown) => {
    if (!editingNote) return
    setEditingNote({ ...editingNote, [field]: value })
    setIsDirty(true)
  }

  const updateMeta = (key: string, value: unknown) => {
    if (!editingNote) return
    const meta = { ...(editingNote.metadata || {}), [key]: value }
    setEditingNote({ ...editingNote, metadata: meta })
    setIsDirty(true)
  }

  const handleSave = async () => {
    if (!editingNote) return
    setSaving(true)
    try {
      const updated = { ...editingNote, last_updated_at: new Date().toISOString() }
      await upsertCloudNote(updated)
      setNotes((prev) => prev.map((n) => n.id === updated.id ? updated : n))
      setSelectedNote(updated)
      setEditingNote({ ...updated })
      setIsDirty(false)
    } catch (e) {
      console.error('Failed to save follow-up:', e)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!selectedNote) return
    try {
      await deleteCloudNote(selectedNote.id)
      setNotes((prev) => prev.filter((n) => n.id !== selectedNote.id))
      setSelectedNote(null)
      setEditingNote(null)
      setConfirmDelete(false)
    } catch (e) {
      console.error('Failed to delete follow-up:', e)
    }
  }

  return (
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      <div style={listAreaStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderBottom: '1px solid rgba(250,250,250,0.1)' }}>
          <div style={{ display: 'flex', gap: 4 }}>
            <button
              onClick={() => setTab('active')}
              style={tabBtnStyle(tab === 'active')}
            >
              Active ({activeNotes.length})
            </button>
            <button
              onClick={() => setTab('history')}
              style={tabBtnStyle(tab === 'history')}
            >
              History ({historyNotes.length})
            </button>
          </div>
          <span style={{ flex: 1 }} />
          {tab === 'active' && <CreateButton label="New Follow-up" onClick={() => setShowCreate(true)} />}
          <button onClick={load} style={refreshBtnStyle} disabled={loading}>
            {loading ? '...' : 'Refresh'}
          </button>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '8px 16px' }}>
          {loading && <div style={{ padding: 24, textAlign: 'center', color: 'rgba(250,250,250,0.4)' }}>Loading...</div>}
          {!loading && tab === 'active' && activeNotes.length === 0 && <EmptyState label="No active follow-ups" />}
          {!loading && tab === 'history' && historyNotes.length === 0 && <EmptyState label="No history yet" />}

          {!loading && tab === 'active' && activeNotes.map((note) => (
            <FollowUpCard key={note.id} note={note} selected={selectedNote?.id === note.id} onSelect={handleSelect} />
          ))}
          {!loading && tab === 'history' && historyNotes.map((note) => (
            <FollowUpCard key={note.id} note={note} selected={selectedNote?.id === note.id} onSelect={handleSelect} />
          ))}
        </div>
      </div>

      {/* Editable detail panel */}
      {editingNote && (
        <div style={detailPanelStyle}>
          <div style={{ padding: 16, borderBottom: '1px solid rgba(250,250,250,0.1)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ flex: 1, fontWeight: 600, fontSize: 14, color: 'rgba(250,250,250,0.5)' }}>Edit Follow-up</span>
            <SaveButton dirty={isDirty} saving={saving} onClick={handleSave} />
            <DeleteButton onClick={() => setConfirmDelete(true)} />
            <button onClick={() => { setSelectedNote(null); setEditingNote(null); setIsDirty(false) }} style={refreshBtnStyle}>X</button>
          </div>
          <FollowUpDetailFields editingNote={editingNote} updateField={updateField} updateMeta={updateMeta} />
        </div>
      )}

      {/* Create follow-up modal */}
      {showCreate && (
        <CreateFollowUpModal
          onClose={() => setShowCreate(false)}
          onCreate={async (note) => {
            await upsertCloudNote(note)
            setNotes((prev) => [note, ...prev])
            setShowCreate(false)
          }}
        />
      )}

      {/* Delete confirm */}
      {confirmDelete && selectedNote && (
        <ConfirmDialog
          message={`Delete follow-up "${selectedNote.title || 'Untitled'}"?`}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </div>
  )
}

// ── FollowUp Detail Fields (parses JSON content) ─────────────────

function FollowUpDetailFields({ editingNote, updateField, updateMeta }: {
  editingNote: CloudNote
  updateField: (field: string, value: unknown) => void
  updateMeta: (key: string, value: unknown) => void
}) {
  const parsed = parseFollowUpContent(editingNote.content)
  const meta = editingNote.metadata as Record<string, unknown> | undefined

  // Helper to update a field inside the JSON content
  const updateContentField = (key: string, value: unknown) => {
    const current = parseFollowUpContent(editingNote.content) || {}
    current[key] = value
    updateField('content', JSON.stringify(current))
  }

  const inputStyle: Record<string, string | number> = {
    width: '100%', padding: '8px 10px', background: 'rgba(0,34,51,0.8)',
    border: '1px solid rgba(250,250,250,0.15)', borderRadius: 6,
    color: '#FAFAFA', fontSize: 13, boxSizing: 'border-box',
  }

  return (
    <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
      <div style={{ marginBottom: 12 }}>
        <label style={fieldLabelStyle}>Title</label>
        <InlineInput value={editingNote.title || ''} onChange={(v) => updateField('title', v)} placeholder="Follow-up title" />
      </div>

      {parsed ? (
        <>
          {/* Parsed JSON fields */}
          <div style={{ marginBottom: 12 }}>
            <label style={fieldLabelStyle}>Description</label>
            <InlineInput
              type="textarea"
              value={(parsed.description as string) || ''}
              onChange={(v) => updateContentField('description', v)}
              placeholder="Description"
            />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={fieldLabelStyle}>Persona</label>
            <InlineInput
              value={(parsed.persona_name as string) || ''}
              onChange={(v) => { updateContentField('persona_name', v) }}
              placeholder="Persona name"
            />
          </div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={fieldLabelStyle}>Date</label>
              <input
                type="date"
                style={inputStyle}
                value={(parsed.date as string) || ''}
                onInput={(e) => updateContentField('date', (e.target as HTMLInputElement).value)}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={fieldLabelStyle}>Time</label>
              <input
                type="time"
                style={inputStyle}
                value={(parsed.time as string) || ''}
                onInput={(e) => updateContentField('time', (e.target as HTMLInputElement).value)}
              />
            </div>
          </div>
          <DetailRow label="Timezone" value={parsed.timezone as string} />
          <DetailRow label="Action" value={parsed.action_type as string} />
          {(parsed.retry_count as number) > 0 && <DetailRow label="Retries" value={String(parsed.retry_count)} />}
        </>
      ) : (
        <div style={{ marginBottom: 12 }}>
          <label style={fieldLabelStyle}>Content</label>
          <InlineInput type="textarea" value={editingNote.content || ''} onChange={(v) => updateField('content', v)} placeholder="Content" />
        </div>
      )}

      {/* Metadata fields — only show Scheduled At if no parsed date/time fields above */}
      {!parsed && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={fieldLabelStyle}>Date</label>
            <input
              type="date"
              style={inputStyle}
              value={(() => {
                const s = meta?.scheduled_at as string | undefined
                return s ? s.slice(0, 10) : ''
              })()}
              onInput={(e) => {
                const date = (e.target as HTMLInputElement).value
                const oldTime = (meta?.scheduled_at as string)?.slice(11, 16) || '00:00'
                updateMeta('scheduled_at', date ? new Date(`${date}T${oldTime}`).toISOString() : undefined)
              }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={fieldLabelStyle}>Time</label>
            <input
              type="time"
              style={inputStyle}
              value={(() => {
                const s = meta?.scheduled_at as string | undefined
                return s ? s.slice(11, 16) : ''
              })()}
              onInput={(e) => {
                const time = (e.target as HTMLInputElement).value
                const oldDate = (meta?.scheduled_at as string)?.slice(0, 10) || new Date().toISOString().slice(0, 10)
                updateMeta('scheduled_at', time ? new Date(`${oldDate}T${time}`).toISOString() : undefined)
              }}
            />
          </div>
        </div>
      )}
      <div style={{ marginBottom: 12 }}>
        <label style={fieldLabelStyle}>Dismissed</label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'rgba(250,250,250,0.7)', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={!!meta?.dismissed}
            onChange={() => updateMeta('dismissed', !meta?.dismissed)}
          />
          Dismissed
        </label>
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={fieldLabelStyle}>Recurrence</label>
        <InlineInput
          value={(meta?.recurrence as string) || ''}
          onChange={(v) => updateMeta('recurrence', v || undefined)}
          placeholder="e.g. daily, weekly"
        />
      </div>
      <div style={{ marginTop: 12, paddingTop: 8, borderTop: '1px solid rgba(250,250,250,0.1)' }}>
        <DetailRow label="Created" value={formatDate(editingNote.created_at)} />
        <DetailRow label="Updated" value={formatDate(editingNote.last_updated_at)} />
      </div>
    </div>
  )
}

// ── FollowUp Card ─────────────────────────────────────────────────

function FollowUpCard({ note, selected, onSelect }: { note: CloudNote; selected: boolean; onSelect: (n: CloudNote) => void }) {
  const meta = note.metadata as Record<string, unknown> | undefined
  const parsed = parseFollowUpContent(note.content)
  const scheduledAt = meta?.scheduled_at as string | undefined
  const isCompleted = parsed?.completed === true
  const isMissed = parsed?.missed === true
  const isHistory = isCompleted || isMissed

  const statusLabel = isCompleted ? 'Completed' : isMissed ? 'Missed' : 'Pending'
  const statusColor = isCompleted ? '#4caf50' : isMissed ? '#f44336' : '#ff9800'

  return (
    <div
      onClick={() => onSelect(note)}
      style={{
        ...cardStyle,
        cursor: 'pointer',
        borderLeft: `3px solid ${statusColor}`,
        background: selected ? 'rgba(45,165,160,0.15)' : cardStyle.background,
        opacity: isHistory ? 0.75 : 1,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ flex: 1, fontWeight: 500, textDecoration: isCompleted ? 'line-through' : undefined }}>
          {note.title || (parsed?.title as string) || 'Untitled'}
        </span>
        {parsed?.persona_name && (
          <span style={{ fontSize: 10, padding: '1px 4px', background: 'rgba(45,165,160,0.3)', borderRadius: 2, color: '#FAFAFA' }}>
            {parsed.persona_name as string}
          </span>
        )}
        <span style={{ fontSize: 10, padding: '1px 4px', background: statusColor, borderRadius: 2, color: '#fff' }}>{statusLabel}</span>
      </div>
      {parsed?.description && (
        <div style={{ fontSize: 12, color: 'rgba(250,250,250,0.6)', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {parsed.description as string}
        </div>
      )}
      {(parsed?.date || scheduledAt) && (
        <div style={{ fontSize: 12, color: 'rgba(250,250,250,0.5)', marginTop: 4 }}>
          {parsed?.date ? `${parsed.date as string} ${parsed.time ? `at ${parsed.time as string}` : ''}` : formatDate(scheduledAt)}
        </div>
      )}
    </div>
  )
}

// ── Create Follow-up Modal ────────────────────────────────────────

function CreateFollowUpModal({ onClose, onCreate }: {
  onClose: () => void
  onCreate: (note: CloudNote) => Promise<void>
}) {
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [scheduledAt, setScheduledAt] = useState('')
  const [recurrence, setRecurrence] = useState('')
  const [creating, setCreating] = useState(false)

  const handleCreate = async () => {
    if (!title.trim()) return
    setCreating(true)
    try {
      const now = new Date().toISOString()
      const note: CloudNote = {
        id: crypto.randomUUID(),
        title: title.trim(),
        content: content.trim() || undefined,
        type: FOLLOWUP_TYPE,
        metadata: {
          scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : undefined,
          recurrence: recurrence || undefined,
          dismissed: false,
        },
        created_at: now,
        last_updated_at: now,
      }
      await onCreate(note)
    } catch (e) {
      console.error('Failed to create follow-up:', e)
    } finally {
      setCreating(false)
    }
  }

  return (
    <ModalDialog
      title="New Follow-up"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} style={{ padding: '6px 16px', border: 'none', borderRadius: 6, background: 'rgba(250,250,250,0.1)', color: 'rgba(250,250,250,0.7)', cursor: 'pointer', fontSize: 13 }}>
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={!title.trim() || creating}
            style={{ padding: '6px 16px', border: 'none', borderRadius: 6, background: '#2DA5A0', color: '#fff', cursor: 'pointer', fontSize: 13, opacity: !title.trim() || creating ? 0.5 : 1 }}
          >
            {creating ? 'Creating...' : 'Create'}
          </button>
        </>
      }
    >
      <FormField label="Title" value={title} onChange={setTitle} required placeholder="Follow-up title" />
      <FormField label="Content" value={content} onChange={setContent} type="textarea" placeholder="Content" />
      <FormField label="Scheduled At" value={scheduledAt} onChange={setScheduledAt} type="datetime-local" />
      <FormField label="Recurrence" value={recurrence} onChange={setRecurrence} placeholder="e.g. daily, weekly" />
    </ModalDialog>
  )
}

const fieldLabelStyle: Record<string, string | number> = {
  fontSize: 12,
  color: 'rgba(250,250,250,0.5)',
  marginBottom: 4,
  display: 'block',
}

const tabBtnStyle = (active: boolean): Record<string, string | number> => ({
  padding: '6px 12px',
  border: '1px solid rgba(250,250,250,0.15)',
  borderRadius: 6,
  background: active ? 'rgba(45,165,160,0.2)' : 'transparent',
  color: active ? '#FAFAFA' : 'rgba(250,250,250,0.6)',
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: active ? 600 : 400,
})
